import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BASE}/api`;

const api = axios.create({ baseURL: BASE, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("editorix_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- auth ----
export const authApi = {
  register: (payload) => api.post("/api/auth/register", payload).then((r) => r.data),
  login: (payload) => api.post("/api/auth/login", payload).then((r) => r.data),
  googleSession: (session_id) => api.post("/api/auth/session", { session_id }).then((r) => r.data),
  me: () => api.get("/api/auth/me").then((r) => r.data),
  logout: () => api.post("/api/auth/logout").then((r) => r.data),
};

// ---- projects ----
export const projectApi = {
  list: () => api.get("/api/projects").then((r) => r.data),
  create: (payload) => api.post("/api/projects", payload).then((r) => r.data),
  get: (id) => api.get(`/api/projects/${id}`).then((r) => r.data),
  update: (id, payload) => api.put(`/api/projects/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/api/projects/${id}`).then((r) => r.data),
  dashboard: (id) => api.get(`/api/projects/${id}/dashboard`).then((r) => r.data),
  demo: (id, count) => api.post(`/api/projects/${id}/demo?count=${count}`).then((r) => r.data),
  importFile: (id, formData) =>
    api.post(`/api/projects/${id}/import`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  styleProfile: (id) => api.post(`/api/projects/${id}/style-profile`).then((r) => r.data),
};

// ---- chapters ----
export const chapterApi = {
  list: (pid) => api.get(`/api/projects/${pid}/chapters`).then((r) => r.data),
  get: (pid, cid) => api.get(`/api/projects/${pid}/chapters/${cid}`).then((r) => r.data),
  add: (pid, payload) => api.post(`/api/projects/${pid}/chapters`, payload).then((r) => r.data),
  update: (pid, cid, payload) => api.put(`/api/projects/${pid}/chapters/${cid}`, payload).then((r) => r.data),
  remove: (pid, cid) => api.delete(`/api/projects/${pid}/chapters/${cid}`).then((r) => r.data),
  reorder: (pid, ids) => api.post(`/api/projects/${pid}/chapters/reorder`, { ordered_ids: ids }).then((r) => r.data),
  merge: (pid, ids) => api.post(`/api/projects/${pid}/chapters/merge`, { chapter_ids: ids }).then((r) => r.data),
  split: (pid, cid, pos) => api.post(`/api/projects/${pid}/chapters/${cid}/split`, { split_position: pos }).then((r) => r.data),
  reanalyze: (pid, cid) => api.post(`/api/projects/${pid}/chapters/${cid}/reanalyze`).then((r) => r.data),
  autoCorrect: (pid, cid) => api.post(`/api/projects/${pid}/chapters/${cid}/auto-correct`).then((r) => r.data),
  compare: (pid, cid) => api.get(`/api/projects/${pid}/chapters/${cid}/compare`).then((r) => r.data),
  restore: (pid, cid) => api.post(`/api/projects/${pid}/chapters/${cid}/restore`).then((r) => r.data),
};

// ---- analysis ----
export const analysisApi = {
  start: (pid) => api.post(`/api/projects/${pid}/analyze`).then((r) => r.data),
  pause: (pid) => api.post(`/api/projects/${pid}/analyze/pause`).then((r) => r.data),
  restart: (pid) => api.post(`/api/projects/${pid}/analyze/restart`).then((r) => r.data),
  status: (pid) => api.get(`/api/projects/${pid}/analyze/status`).then((r) => r.data),
};

// ---- suggestions ----
export const suggestionApi = {
  action: (pid, sid, payload) => api.post(`/api/projects/${pid}/suggestions/${sid}/action`, payload).then((r) => r.data),
};

// ---- memory / consistency ----
export const memoryApi = {
  characters: (pid) => api.get(`/api/projects/${pid}/characters`).then((r) => r.data),
  places: (pid) => api.get(`/api/projects/${pid}/places`).then((r) => r.data),
  objects: (pid) => api.get(`/api/projects/${pid}/objects`).then((r) => r.data),
  timeline: (pid) => api.get(`/api/projects/${pid}/timeline`).then((r) => r.data),
  inconsistencies: (pid) => api.get(`/api/projects/${pid}/inconsistencies`).then((r) => r.data),
  runConsistency: (pid) => api.post(`/api/projects/${pid}/consistency/run`).then((r) => r.data),
  incAction: (pid, iid, payload) => api.post(`/api/projects/${pid}/inconsistencies/${iid}/action`, payload).then((r) => r.data),
};

// ---- misc ----
export const miscApi = {
  search: (pid, q) => api.get(`/api/projects/${pid}/search?q=${encodeURIComponent(q)}`).then((r) => r.data),
  dictionary: (pid) => api.get(`/api/projects/${pid}/dictionary`).then((r) => r.data),
  addWord: (pid, word) => api.post(`/api/projects/${pid}/dictionary`, { word }).then((r) => r.data),
  removeWord: (pid, wid) => api.delete(`/api/projects/${pid}/dictionary/${wid}`).then((r) => r.data),
  changelog: (pid) => api.get(`/api/projects/${pid}/changelog`).then((r) => r.data),
  report: (pid) => api.get(`/api/projects/${pid}/report`).then((r) => r.data),
  buildReport: (pid) => api.post(`/api/projects/${pid}/report`).then((r) => r.data),
  finalCheck: (pid) => api.post(`/api/projects/${pid}/final-check`).then((r) => r.data),
  exportBlob: (pid, format, variant) =>
    api.get(`/api/projects/${pid}/export?format=${format}&variant=${variant}`, { responseType: "blob" }),
};

export default api;
